import { HikeTimeUtility } from "../../../utils/helpers/HikeTime";
import { i18n } from "../../../i18n";
import type { NightStop, StayKind } from "../services/night-planner";
import { MAX_DAYS, MIN_DAYS } from "./trailLine";

/**
 * The AI Planner's questions and sums (AiPlannerModal.vue): three about how
 * the walker walks, which give a number of days, then where they'd like to
 * stay — which the planner then looks for along the route.
 */

// Labels are functions with literal keys, as the client's translations only
// carry keys that a t() call names.
export interface PlannerQuestion {
  key: 'flat' | 'climb' | 'hours';
  question: () => string;
  options: Array<{ key: string; label: () => string }>;
}

export const PACE_QUESTIONS: PlannerQuestion[] = [
  {
    key: 'flat',
    question: () => i18n.global.t('plan_trail.ai_planner.flat_question'),
    options: [
      { key: 'fourKmPerHour', label: () => i18n.global.t('plan_trail.ai_planner.flat_leisurely') },
      { key: 'fiveKmPerHour', label: () => i18n.global.t('plan_trail.ai_planner.flat_steady') },
      { key: 'sixKmPerHour', label: () => i18n.global.t('plan_trail.ai_planner.flat_brisk') },
    ],
  },
  {
    key: 'climb',
    question: () => i18n.global.t('plan_trail.ai_planner.climb_question'),
    options: [
      { key: 'fiveHundredMetersPerHour', label: () => i18n.global.t('plan_trail.ai_planner.climb_slow') },
      { key: 'sixHundredMetersPerHour', label: () => i18n.global.t('plan_trail.ai_planner.climb_average') },
      { key: 'eightHundredMetersPerHour', label: () => i18n.global.t('plan_trail.ai_planner.climb_strong') },
    ],
  },
  {
    key: 'hours',
    question: () => i18n.global.t('plan_trail.ai_planner.hours_question'),
    options: [
      { key: '4.5', label: () => i18n.global.t('plan_trail.ai_planner.hours_short') },
      { key: '6.5', label: () => i18n.global.t('plan_trail.ai_planner.hours_full') },
      { key: '8.5', label: () => i18n.global.t('plan_trail.ai_planner.hours_long') },
    ],
  },
];

export type StayChoice = StayKind | 'none';

export const STAY_CHOICES: Array<{ key: StayChoice; label: () => string }> = [
  { key: 'beds', label: () => i18n.global.t('plan_trail.ai_planner.stay_beds') },
  { key: 'camping', label: () => i18n.global.t('plan_trail.ai_planner.stay_camping') },
  { key: 'any', label: () => i18n.global.t('plan_trail.ai_planner.stay_any') },
  { key: 'none', label: () => i18n.global.t('plan_trail.ai_planner.stay_none') },
];

export interface PaceAnswers {
  flat: string | null;
  climb: string | null;
  hours: string | null;
}

/** The walker's pace, from their answers (a steady 5 km/h and 600 m/h until they say). */
export function paceFor(answers: PaceAnswers): HikeTimeUtility {
  return HikeTimeUtility.fromPair(answers.flat || 'fiveKmPerHour', answers.climb || 'sixHundredMetersPerHour');
}

export function hoursPerDay(answers: PaceAnswers): number {
  return parseFloat(answers.hours ?? '') || 6.5;
}

/** Hours of walking in all, and the days that comes to at the walker's hours a day. */
export function suggestedPlan(answers: PaceAnswers, distanceM: number, ascentM: number): { hours: number; days: number } {
  const hours = paceFor(answers).secondsDuration(ascentM, distanceM) / 3600;
  const days = Math.ceil(hours / hoursPerDay(answers));
  return { hours, days: clampDays(days || MIN_DAYS) };
}

export function clampDays(days: number): number {
  return Math.min(MAX_DAYS, Math.max(MIN_DAYS, Math.round(days)));
}

export interface PlannedDay {
  day: number;
  /** Metres walked along the route that day (any walk off it to a place to stay aside). */
  lengthM: number;
  /** Where the day ends — null for the last, which ends at the finish. */
  stop: NightStop | null;
}

/** The days a plan's nights make, each with how far it walks. */
export function plannedDays(stops: NightStop[], totalM: number): PlannedDay[] {
  const ends = [...stops.map(stop => stop.atM), totalM];
  return ends.map((endM, i) => ({
    day: i + 1,
    lengthM: Math.max(0, endM - (i === 0 ? 0 : ends[i - 1])),
    stop: stops[i] ?? null,
  }));
}

/** How many of the nights have somewhere to stay — found, or chosen. */
export function nightsWithPlaces(stops: NightStop[]): number {
  return stops.filter(stop => stop.place || stop.pick).length;
}
